import { BehaviorSubject, Observable } from 'rxjs';
import { Injectable, Optional, SkipSelf } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ReplaceMap, ReplaceObj } from 'src/lib/fliko/interfaces/replaceMap';
import { SessionStorageService } from './session-storage.service';

@Injectable({ providedIn: 'root' })
export class LocalizationService {
  private localeMap = new Map([
    ['pl', 'pl-PL'],
    ['en', 'en-GB']
  ]);
  private _currentLang: string = 'pl';
  private _defaultLang: string = 'pl';

  private languageChange$ = new BehaviorSubject<string>('');

  constructor(
    private ts: TranslateService,
    private sessionStorageSrv: SessionStorageService,
    @Optional() @SkipSelf() thisService?: LocalizationService
  ) {
    if (thisService) throw new Error('Multiple instances of LocalizationService');
    this.ts.setDefaultLang(this._defaultLang);
    const currLang = sessionStorageSrv.locale.get();
    this.currentLang = currLang ?? navigator.language;
  }

  private sanitizeLang(val: string) {
    if (val.includes('-')) return val.split('-')[0];
    else return val;
  }

  private applyLang(val: string, persist = true): void {
    const lang = this.sanitizeLang(val);
    this._currentLang = this.localeMap.has(lang) ? lang : 'pl';
    this.ts.use(this._currentLang);
    if (persist) this.sessionStorageSrv.locale.set(this._currentLang);
    this.languageChange$.next(this._currentLang);
  }

  public get currentLocale(): string {
    return this.localeMap.get(this._currentLang) ?? 'pl-PL';
  }

  public get currentLang(): string {
    return this._currentLang;
  }

  public set currentLang(val: string) {
    this.applyLang(val);
  }

  public get defaultLang(): string {
    return this._defaultLang;
  }

  public set defaultLang(val: string) {
    this._defaultLang = this.sanitizeLang(val);
    this.ts.setDefaultLang(this._defaultLang);
  }

  public get onLanguageChanged$(): Observable<string> {
    return this.languageChange$.asObservable();
  }

  public getPreferredPublicLang(lang?: string | null): 'pl' | 'en' {
    const browserLang = lang ?? (typeof navigator !== 'undefined' ? navigator.language : '');
    return this.sanitizeLang(browserLang) === 'pl' ? 'pl' : 'en';
  }

  public applyPreferredPublicLang(lang?: string | null): void {
    this.applyLang(this.getPreferredPublicLang(lang));
  }

  /**
   * Translate with replace all variables ($foo) with given values or double translate if variable is in tildes (~$foo~)
   * @param {string | ReplaceObj} val translation key or whole ReplaceObj
   * @param {ReplaceMap}          map map key-value, where key is variable name with dollar sign
   * @return {string} translated data
   */
  public trReplace(val: string | ReplaceObj | null | undefined, map?: ReplaceMap): string {
    if (!val) return '';
    let key = '';
    let replaceMap = {};
    if (typeof val == 'string') {
      key = val;
      replaceMap = map ?? {};
    } else {
      key = val.key;
      replaceMap = val.replaceMap;
    }

    let ret: string = this.translate(key);
    for (const k in replaceMap) {
      const value = (replaceMap as any)[k];
      if (value != null) {
        ret = ret.replace(k, value.toString());
      }
    }
    // ? tilde means we have additional translation within translation (eg. replacement with proper form)
    if (ret.includes('~')) {
      const split = ret.split('~');
      // * [ trash, translate, trash, translate, trash ...]
      for (let i = 1; i < split.length; i += 2) {
        const trans = this.translate(split[i]);
        ret = ret.replace(split[i], trans);
      }
      // ? clear all tildes
      ret = ret.replace(/~/g, '');
    }
    return ret;
  }

  public translate(key: string): string {
    return key ? this.ts.instant(key) : '';
  }

  /**
   * Extract localised data depending on current locale
   * @param data object with language code keys
   */
  // prettier-ignore
  public getData<T>(data: { [locale: string]: T }): T | undefined {
    let             localised = data[this._currentLang];
    if (!localised) localised = data[this._defaultLang];
    if (!localised) localised = Object.values(data)[0];
    return          localised;
  }
}
